import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Bike, MapPin, Phone, Star } from "lucide-react";
import { connectSocket, getSocket } from "../../config/socket.config";
import { getOrderTracking, rateOrderRider } from "../../services/order.api";

const card = {
  background: "#fff", border: "1.5px solid #f1f5f9",
  borderRadius: 14, padding: 20, fontFamily: "'Inter', sans-serif",
};

const heading = {
  margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "#9ca3af",
  textTransform: "uppercase", letterSpacing: 0.5,
};

const minutesAgo = (value) => {
  if (!value) return null;
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (Number.isNaN(mins)) return null;
  return mins <= 0 ? "just now" : `${mins} min ago`;
};

/**
 * The OwnFood rider on a platform-delivered order: who they are, a way to call
 * them while they are on the job, where they last were, and — once the food
 * has arrived — a rating. Renders nothing for a kitchen-delivered order.
 *
 * Positions arrive live on the customer's socket (`order:rider_location`) and
 * are otherwise refreshed with the tracking read.
 */
export default function RiderCard({ order }) {
  const queryClient = useQueryClient();
  const orderId = order._id;
  const platform = order.deliveryMethod === "PLATFORM";
  const live = order.rawStatus === "ready" || order.rawStatus === "out_for_delivery";
  const delivered = order.rawStatus === "delivered";

  const { data: tracking } = useQuery({
    queryKey: ["order-rider", orderId],
    queryFn: () => getOrderTracking(orderId),
    enabled: platform && (live || delivered),
    refetchInterval: live ? 30000 : false,
  });

  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!platform || !live) return undefined;
    connectSocket();
    const socket = getSocket();
    const onLocation = (payload) => {
      if (payload?.orderId !== orderId) return;
      setPosition({ latitude: payload.latitude, longitude: payload.longitude, at: payload.at });
    };
    socket.on("order:rider_location", onLocation);
    return () => socket.off("order:rider_location", onLocation);
  }, [platform, live, orderId]);

  const [stars, setStars] = useState(0);
  const rate = useMutation({
    mutationFn: (rating) => rateOrderRider(orderId, rating),
    onSuccess: () => {
      toast.success("Thanks for rating your rider!");
      queryClient.invalidateQueries({ queryKey: ["order-tracking", orderId] });
    },
    onError: (error) => toast.error(error?.message || "Could not save your rating"),
  });

  if (!platform) return null;

  const rider = tracking?.rider;
  const alreadyRated = order.delivery?.riderRating != null;

  if (delivered) {
    if (alreadyRated || rate.isSuccess) {
      return (
        <div style={card}>
          <p style={heading}>Your rider</p>
          <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
            You rated this delivery {order.delivery?.riderRating ?? stars} / 5. Thank you!
          </p>
        </div>
      );
    }
    return (
      <div style={card}>
        <p style={heading}>How was your rider?</p>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              onClick={() => setStars(n)}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            >
              <Star size={24} color="#f59e0b" fill={n <= stars ? "#f59e0b" : "none"} />
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!stars || rate.isPending}
          onClick={() => rate.mutate(stars)}
          style={{
            padding: "8px 16px", borderRadius: 10, border: "none",
            background: stars ? "#0d6efd" : "#e5e7eb", color: stars ? "#fff" : "#9ca3af",
            fontSize: 13, fontWeight: 700, cursor: stars ? "pointer" : "default",
          }}
        >
          {rate.isPending ? "Saving…" : "Submit rating"}
        </button>
      </div>
    );
  }

  if (!live) return null;

  // Live socket position wins over the last tracking read; both are the rider's
  // own phone reporting in, so the newer one is the better one.
  const where = position || (rider?.latitude != null ? { latitude: rider.latitude, longitude: rider.longitude } : null);
  const seen = minutesAgo(position?.at);
  // No rider is booked for an online order until it is paid — say so, rather
  // than "finding a rider" for a search that has not started.
  const awaitingPayment =
    !tracking && order.paymentMethod === "online" && order.paymentStatus !== "paid";

  if (awaitingPayment) {
    return (
      <div style={card}>
        <p style={heading}>Your OwnFood rider</p>
        <p style={{ margin: 0, fontSize: 13, color: "#92400e" }}>
          Your food is ready. Complete your online payment and we'll send a rider right away.
        </p>
      </div>
    );
  }

  return (
    <div style={card}>
      <p style={heading}>Your OwnFood rider</p>
      {!rider ? (
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
          <Bike size={15} /> Finding a rider near the kitchen…
        </p>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
              <Bike size={16} /> {rider.name}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6b7280" }}>
              {tracking.status === "PICKED_UP" ? "On the way to you" : "Heading to the kitchen"}
            </p>
            {where && (
              <a
                href={`https://www.google.com/maps?q=${where.latitude},${where.longitude}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, color: "#0d6efd", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4 }}
              >
                <MapPin size={12} /> See where they are{seen ? ` (${seen})` : ""}
              </a>
            )}
          </div>
          {rider.phone && (
            <a
              href={`tel:${rider.phone}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "9px 16px", borderRadius: 10, background: "#16a34a",
                color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none",
              }}
            >
              <Phone size={14} /> Call rider
            </a>
          )}
        </div>
      )}
    </div>
  );
}
